import React from "react";
import { Camera, Eye, MapPin, ChevronRight } from "lucide-react";

const GOOGLE_CAR_IMG = "https://customer-assets.emergentagent.com/job_e81f3fb1-e80b-4939-bc29-284173dc4f78/artifacts/mrymfsxj_image.png";

const EXAMPLES = [
  {
    name: "Archeodromo di Poggibonsi",
    address: "Via Fortezza Medicea, 53036 Poggibonsi SI",
    views: "1.627.287",
    question: "Chi ha permesso a quasi 2 milioni di persone di visitarlo virtualmente?",
  },
  {
    name: "ChiantiBanca",
    address: "Via XXV Aprile, 108, 53034 Colle di Val d'Elsa SI",
    views: "262.061",
    question: "Chi ha detto ai viaggiatori che li c'e una banca?",
  },
  {
    name: "Az. Agr. Colombaia",
    address: "Loc. S. Biagio, 18A, 53034 Colle di Val d'Elsa SI",
    views: "245.102",
    question: "Chi ha segnalato uno dei migliori ristoranti in zona?",
  },
];

const ChiFaLeFotoSection = () => (
  <section className="py-16 bg-[#080c16] relative overflow-hidden" data-testid="chi-fa-le-foto-section">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-[#4285F4]/15 text-[#4285F4] px-4 py-1.5 rounded-full font-bold text-xs mb-4">
          <Camera className="w-4 h-4" /> LA DOMANDA CHE NESSUNO SI FA
        </div>
        <h2 className="text-2xl md:text-4xl font-black text-white mb-3" style={{ fontFamily: "'Poppins',sans-serif" }}>
          Chi mette le foto su <span className="text-[#4285F4]">Google</span>?
        </h2>
        <p className="text-gray-400 max-w-2xl mx-auto text-sm leading-relaxed">
          Ogni volta che cerchi un ristorante, un museo o una banca su Google Maps e vedi le foto a 360 gradi "dentro"...
          <strong className="text-white"> chi le ha scattate?</strong>
        </p>
      </div>

      {/* Google Car vs Photographer */}
      <div className="grid lg:grid-cols-2 gap-6 mb-12">
        {/* Google Car */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6 text-center">
          <img
            src={GOOGLE_CAR_IMG}
            alt="Google Maps Street View Car"
            className="w-full max-w-xs mx-auto rounded-xl mb-4"
          />
          <h3 className="text-white font-bold text-lg mb-2">L'auto di Google</h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            Passa <strong className="text-white">una volta all'anno</strong> per le strade.
            Scatta foto delle vie, degli incroci, dei marciapiedi.
          </p>
          <p className="text-gray-500 text-xs mt-3">
            Ma <strong className="text-[#EA4335]">non entra</strong> nei negozi. Non sale al piano di sopra.
            Non fotografa il tuo menu, i tuoi interni, la tua sala.
          </p>
        </div>

        {/* Riccardo */}
        <div className="bg-[#34A853]/5 border border-[#34A853]/20 rounded-2xl p-6 text-center">
          <div className="w-24 h-24 mx-auto rounded-2xl bg-white/10 flex items-center justify-center mb-4 overflow-hidden">
            <Camera className="w-12 h-12 text-[#34A853]" />
          </div>
          <h3 className="text-white font-bold text-lg mb-2">Un Fotografo Google Street View Certificato</h3>
          <p className="text-gray-400 text-sm leading-relaxed">
            Passeggia per la tua citta con la <strong className="text-white">GoPro MAX</strong>.
            Entra nel tuo locale. Scatta foto immersive a 360 gradi che verranno visualizzate
            <strong className="text-[#34A853]"> centinaia di migliaia di volte</strong>.
          </p>
          <p className="text-gray-500 text-xs mt-3">
            Le aggiorna, le ottimizza, le posiziona.
            <strong className="text-white"> Ogni giorno. Non una volta all'anno.</strong>
          </p>
        </div>
      </div>

      {/* Real examples */}
      <div className="mb-8">
        <h3 className="text-white font-bold text-center mb-6">Esempi reali — foto scattate da Riccardo Rossi</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {EXAMPLES.map((ex) => (
            <div key={ex.name} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5 hover:border-[#4285F4]/30 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-[#EA4335] flex-shrink-0" />
                <div>
                  <p className="text-white font-bold text-sm">{ex.name}</p>
                  <p className="text-gray-600 text-[10px]">{ex.address}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2 mb-3">
                <Eye className="w-4 h-4 text-[#4285F4]" />
                <span className="text-[#4285F4] font-black text-lg">{ex.views}</span>
                <span className="text-gray-500 text-xs">visualizzazioni</span>
              </div>
              <p className="text-gray-400 text-xs italic leading-relaxed">
                {ex.question}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom thought */}
      <div className="bg-gradient-to-r from-[#4285F4]/10 via-[#EA4335]/10 via-[#FBBC04]/10 to-[#34A853]/10 rounded-2xl border border-white/10 p-6 md:p-8 text-center">
        <p className="text-white text-base md:text-lg font-bold leading-relaxed mb-3">
          La prossima volta che cerchi qualcosa su Google e vedi delle foto...
        </p>
        <p className="text-gray-300 text-sm leading-relaxed max-w-2xl mx-auto">
          Chiediti: <strong className="text-[#FBBC04]">chi le ha messe li?</strong>
          <br />Non e l'auto di Google. Non e un robot. Non e l'intelligenza artificiale.
          <br />E un fotografo certificato che ha camminato in quel posto con una fotocamera a 360 gradi.
        </p>
        <p className="text-[#34A853] font-bold text-sm mt-4">
          52.928.752 visualizzazioni. 7.000+ foto. Un fotografo. Dal 2016.
        </p>
        <a
          href="#pacchetti"
          onClick={(e) => { e.preventDefault(); document.getElementById('pacchetti')?.scrollIntoView({ behavior: 'smooth' }); }}
          className="inline-flex items-center gap-2 bg-white text-gray-900 font-black px-6 py-3 rounded-full mt-5 hover:scale-105 transition-transform text-sm"
          data-testid="chi-foto-cta"
        >
          Voglio anche io <ChevronRight className="w-4 h-4" />
        </a>
      </div>

    </div>
  </section>
);

export default ChiFaLeFotoSection;
